import {
  findNode,
  getAboveNode,
  getEditorString,
  getNodeLeaf,
  getNodeProps,
  getPluginType,
  type InsertNodesOptions,
  isDefined,
  isExpanded,
  isType,
  type PlateEditor,
  removeNodes,
  setNodes,
  type UnwrapNodesOptions,
  type Value,
  type WrapNodesOptions,
} from '@udecode/plate-common';
import {
  CreateLinkNodeOptions,
  insertLink,
  validateUrl,
  wrapLink,
} from '@udecode/plate-link';

import {
  ELEMENT_ANCHOR_LINK,
  ELEMENT_INTERLINK,
  ELEMENT_LINK,
  MyLinkElement,
} from '../../../plateTypes';
import { unwrapLink } from './unwrapLink';
import { upsertLinkText } from './upsertLinkText';

export type UpsertLinkOptions<V extends Value = Value> = {
  insertNodesOptions?: InsertNodesOptions<V>;
  /** If true, insert text when selection is in url. */
  insertTextInLink?: boolean;
  skipValidation?: boolean;
  unwrapNodesOptions?: UnwrapNodesOptions<V>;
  wrapNodesOptions?: WrapNodesOptions<V>;
} & CreateLinkNodeOptions;

/**
 * If selection in a link or is not url:
 *
 * - Insert text with url, exit If selection is expanded or `update` in a link:
 * - Remove link node, get link text Then:
 * - Insert link node
 */
export const upsertLink = <V extends Value>(
  editor: PlateEditor<V>,
  {
    insertNodesOptions,
    insertTextInLink,
    skipValidation = false,
    target,
    text,
    url,
  }: UpsertLinkOptions<V>,
): boolean | void => {
  const at = editor.selection;
  if (!at) return;

  const linkAbove = getAboveNode<MyLinkElement>(editor, {
    at,
    match: (node) => {
      return isType(editor, node, [
        ELEMENT_LINK,
        ELEMENT_ANCHOR_LINK,
        ELEMENT_INTERLINK,
      ]);
    },
  });

  // anchor and focus in link -> insert text
  if (insertTextInLink && linkAbove) {
    // we don't want to insert marks in links
    editor.insertText(url);
    return true;
  }
  if (!skipValidation && !validateUrl(editor, url)) return;
  if (isDefined(text) && text.length === 0) {
    text = url;
  }

  // edit the link url and/or target
  if (linkAbove) {
    if (
      linkAbove[0].type !== ELEMENT_LINK ||
      url !== linkAbove[0]?.url ||
      target !== linkAbove[0]?.target
    ) {
      setNodes<MyLinkElement>(
        editor,
        { type: ELEMENT_LINK, target, url },
        { at: linkAbove[1] },
      );
    }
    upsertLinkText(editor, { text });
    return true;
  }

  // selection contains at least one edge or lies between the edges
  const linkEntry = findNode<MyLinkElement>(editor, {
    at,
    match: { type: getPluginType(editor, ELEMENT_LINK) },
  });

  const [linkNode, linkPath] = linkEntry ?? [];

  let shouldReplaceText = false;

  if (linkPath && text?.length) {
    const linkText = getEditorString(editor, linkPath);

    if (text !== linkText) {
      shouldReplaceText = true;
    }
  }
  if (isExpanded(at)) {
    // anchor and focus in link
    if (linkAbove) {
      unwrapLink(editor, {
        at: linkAbove[1],
      });
    } else {
      unwrapLink(editor, {
        split: true,
      });
    }

    wrapLink(editor, {
      target,
      url,
    });

    upsertLinkText(editor, { text });

    return true;
  }
  if (shouldReplaceText) {
    removeNodes(editor, {
      at: linkPath,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const props = getNodeProps(linkNode ?? ({} as any));

  const path = editor.selection?.focus.path;

  if (!path) return;

  // link text should have the focused leaf marks
  const leaf = getNodeLeaf(editor, path);

  // if text is empty, text is url
  if (!text?.length) {
    text = url;
  }

  insertLink(
    editor,
    {
      ...props,
      children: [
        {
          ...leaf,
          text,
        },
      ],
      target,
      url,
    },
    insertNodesOptions,
  );

  return true;
};
